import { ChevronLeft, ChevronRight, MapPinPlus } from "lucide-react";
import { type KeyboardEvent, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { create } from "zustand";

import { MenuItem, MenuSeparator } from "../../components/Menu";
import { Motion, Presence, useClosing } from "../../components/presence";
import { useDismiss } from "../../components/useDismiss";
import { t } from "../../i18n/i18n";
import { useAnnotationTip } from "./annotationAccess";
import { type AnnotationCategoryKey, CATEGORIES, labelled, sidcOf, symbolsOf } from "./catalog";
import { annotationSvg } from "./symbolStyle";
import { useAnnotations } from "./useAnnotations";
import { annotationControl, useUi } from "../../state/ui";

/** Clic droit sur la carte (hors d'une unité), appui long au doigt sur un endroit vide ou bouton « Annotation » de
 *  la barre du bas : « Ajouter une annotation ici » — catégories, puis symbole ; la pose se fait au point visé (le
 *  clic sur la carte après le choix pour le bouton) et la fiche s'ouvre. Rendu une fois par l'application, au point du pointeur ; la carte
 *  écrit la demande dans `useAnnotationMenu`. */

/** `center` : menu ouvert par le bouton « Annotation », le symbole choisi passe en pose (clic sur la carte, comme depuis la palette). */
export type AnnotationMenuRequest = { x: number; y: number; lngLat: [number, number]; center?: boolean };

export const useAnnotationMenu = create<{
  request: AnnotationMenuRequest | null;
  open: (request: AnnotationMenuRequest) => void;
  close: () => void;
}>()((set) => ({
  request: null,
  open: (request) => {
    // La bulle d'astuce a rempli son office : elle se ferme d'elle-même.
    useAnnotationTip.getState().dismiss();
    set({ request });
  },
  close: () => set({ request: null }),
}));

export function AnnotationMenu() {
  const request = useAnnotationMenu((s) => s.request);
  return createPortal(
    <Presence show={Boolean(request)}>
      {request && <Menu key={`${request.x}|${request.y}`} request={request} />}
    </Presence>,
    document.body,
  );
}

const WIDTH = 268;
const MARGIN = 8;

/** Aperçu d'un symbole (SVG de milsymbol), dans une case de 28 × 20 px (`square` : 20 × 20, cases d'icône des
 *  lignes de liste). */
export function SymbolThumb({ sidc, size = 16, square = false }: { sidc: string; size?: number; square?: boolean }) {
  return (
    <span
      aria-hidden
      className={
        square
          ? "flex h-5 w-5 shrink-0 items-center justify-center [&>svg]:max-h-5 [&>svg]:max-w-5"
          : "flex h-5 w-7 shrink-0 items-center justify-center [&>svg]:max-h-5 [&>svg]:max-w-7"
      }
      dangerouslySetInnerHTML={{ __html: annotationSvg(sidc, size) }}
    />
  );
}

/** Libellé d'entrée précédé de l'aperçu du symbole (la case d'icône d'une entrée ne fait que 16 px). */
function ThumbLabel({ sidc, text }: { sidc: string; text: string }) {
  return (
    <span className="flex items-center gap-2">
      <SymbolThumb sidc={sidc} />
      <span className="min-w-0 flex-1">{text}</span>
    </span>
  );
}

function Menu({ request }: { request: AnnotationMenuRequest }) {
  const close = useAnnotationMenu((s) => s.close);
  const { create } = useAnnotations();
  /** Menu du bouton « Annotation » : le symbole choisi passe en pose, l'utilisateur clique ensuite sur la carte
   *  (comme depuis la palette), plutôt qu'une pose au centre qui serait refusée hors du camp ou du terrain. */
  const startPlacing = (symbol: ReturnType<typeof symbolsOf>[number]) => {
    const ui = useUi.getState();
    const control = annotationControl(symbol.category);
    if (ui.controlVisible[control] === false) ui.setControlVisible(control, true);
    ui.select(null);
    ui.setDraft({ family: "annotation", kind: symbol.key, name: labelled(symbol), color: "", sidc: sidcOf(symbol.key), points: [], hover: null });
  };
  const [category, setCategory] = useState<AnnotationCategoryKey | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const closing = useClosing();
  useDismiss(!closing, close, ref);

  useEffect(() => {
    const outside = (e: Event) => {
      if (!ref.current?.contains(e.target as Node)) close();
    };
    window.addEventListener("resize", close);
    window.addEventListener("wheel", outside, { capture: true, passive: true });
    window.addEventListener("touchmove", outside, { capture: true, passive: true });
    return () => {
      window.removeEventListener("resize", close);
      window.removeEventListener("wheel", outside, { capture: true });
      window.removeEventListener("touchmove", outside, { capture: true });
    };
  }, [close]);
  useEffect(() => {
    ref.current?.querySelector<HTMLButtonElement>('[role="menuitem"]:not(:disabled)')?.focus({ preventScroll: true });
  }, [category]);

  const [where] = useState(() => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const left = Math.max(MARGIN, Math.min(request.x, vw - WIDTH - MARGIN));
    const down = vh - request.y > 340 || request.y < vh / 2;
    return down
      ? { left, top: Math.min(request.y + 2, vh - MARGIN - 200), origin: "top left" }
      : { left, bottom: vh - request.y + 2, origin: "bottom left" };
  });

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowLeft" && category) {
      e.preventDefault();
      setCategory(null);
      return;
    }
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const items = [...(ref.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)') ?? [])];
    const i = items.indexOf(document.activeElement as HTMLButtonElement);
    items[(i + (e.key === "ArrowDown" ? 1 : -1) + items.length) % items.length]?.focus();
  };
  const current = CATEGORIES.find((c) => c.key === category);
  const title = request.center ? t("Choisir l'annotation à poser") : t("Ajouter une annotation ici");

  return (
    <Motion
      ref={ref}
      origin="zoom"
      role="menu"
      aria-label={title}
      onKeyDown={onKeyDown}
      onContextMenu={(e) => e.preventDefault()}
      className="fixed z-[100] flex max-h-[min(70vh,440px)] flex-col rounded-xl border border-line bg-surface p-1 shadow-float"
      style={{
        left: where.left,
        width: WIDTH,
        transformOrigin: where.origin,
        ...(where.top !== undefined ? { top: where.top } : { bottom: where.bottom }),
      }}
      data-annotation-menu=""
    >
      <div className="flex min-h-8 items-center gap-2 px-2 py-1">
        <MapPinPlus size={14} className="shrink-0 text-ink-muted" aria-hidden />
        <span className="min-w-0 flex-1 text-ui font-semibold text-ink">{title}</span>
      </div>
      <MenuSeparator />
      {current ? (
        <>
          <MenuItem icon={ChevronLeft} label={labelled(current)} keepOpen onSelect={() => setCategory(null)} />
          <MenuSeparator />
          <div className="min-h-0 overflow-y-auto">
            {symbolsOf(current.key).map((symbol) => (
              <MenuItem
                key={symbol.key}
                label={<ThumbLabel sidc={sidcOf(symbol.key)} text={labelled(symbol)} />}
                onSelect={() => {
                  close();
                  if (request.center) startPlacing(symbol);
                  else create(symbol.key, request.lngLat);
                }}
              />
            ))}
          </div>
        </>
      ) : (
        CATEGORIES.map((c) => (
          <MenuItem
            key={c.key}
            label={<ThumbLabel sidc={sidcOf(symbolsOf(c.key)[0]!.key)} text={labelled(c)} />}
            hint={t("{n} symboles", { n: symbolsOf(c.key).length })}
            keepOpen
            onSelect={() => setCategory(c.key)}
            trailing={<ChevronRight size={14} className="shrink-0 text-ink-muted" aria-hidden />}
          />
        ))
      )}
    </Motion>
  );
}
